import React from "react";
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  Grid,
  Header,
  Icon,
  SpaceBetween,
  Link
} from "@cloudscape-design/components";
import { ACCOUNTS, DIAGRAM_MANAGEMENT, IMPORT, RESOURCES } from "../../routes";
import useLink from "../Hooks/useLink";
import { css } from "@emotion/react";
import {
  colorBackgroundHomeHeader,
  colorBorderDividerDefault,
  colorTextHomeHeaderDefault,
  colorTextHomeHeaderSecondary
} from "@cloudscape-design/design-tokens";
import { isUsingOrganizations } from "../../Utils/AccountUtils";
const headerStyle = css`
  background: ${colorBackgroundHomeHeader};
  
  div.header-title {
    color: ${colorTextHomeHeaderDefault}
  }
  
  .header-sub-title,
  .category {
    color: ${colorTextHomeHeaderSecondary}
  }
  
  div.header-cta {
    margin-bottom: 38px;
  }
  
  .header-title > * {
    max-width: 700px;
  }
`;
const sidebarStyle = css`
  @media (min-width: 957px) {
    margin-top: -86px;
  }
`;
const listStyle = css`
  list-style-type: none;
  margin: 0;
  padding: 0;

  li {
    border-top: 1px solid ${colorBorderDividerDefault};
    padding: 0.8rem 0;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;

    &:first-of-type {
      padding-top: 0;
      border-top: none;
    }

    &:last-of-type{
      padding-bottom: 0;
    }
  }
`;
const Homepage = () => {
  const { handleFollow } = useLink();
  return /* @__PURE__ */ React.createElement(Box, { margin: { bottom: "l" } }, /* @__PURE__ */ React.createElement("div", { css: headerStyle }, /* @__PURE__ */ React.createElement(Box, { padding: { vertical: "xxxl", horizontal: "s" } }, /* @__PURE__ */ React.createElement(
    Grid,
    {
      gridDefinition: [
        { offset: { l: "2", xxs: "1" }, colspan: { l: "8", xxs: "10" } },
        { colspan: { xl: "6", l: "5", s: "6", xxs: "10" }, offset: { l: "2", xxs: "1" } },
        ...isUsingOrganizations() ? [] : [{ colspan: { xl: "2", l: "3", s: "4", xxs: "10" }, offset: { s: "0", xxs: "1" } }]
      ]
    },
    /* @__PURE__ */ React.createElement(Box, { fontWeight: "light", padding: { top: "xs" } }, /* @__PURE__ */ React.createElement("span", { className: "category" }, "Management & Governance")),
    /* @__PURE__ */ React.createElement("div", { className: "header-title" }, /* @__PURE__ */ React.createElement(
      Box,
      {
        variant: "h1",
        fontWeight: "heavy",
        padding: "n",
        fontSize: "display-l",
        color: "inherit"
      },
      "Workload Discovery on AWS"
    ), /* @__PURE__ */ React.createElement(
      Box,
      {
        fontWeight: "light",
        padding: { bottom: "s" },
        fontSize: "display-l",
        color: "inherit"
      },
      "Explore your AWS Workloads"
    ), /* @__PURE__ */ React.createElement(Box, { variant: "p", fontWeight: "light" }, /* @__PURE__ */ React.createElement("span", { className: "header-sub-title" }, "Workload Discovery on AWS is a tool to visualize AWS Cloud workloads. Use Workload Discovery on AWS to build, customize, and share detailed architecture diagrams of your workloads based on live data from AWS."))),
    isUsingOrganizations() ? void 0 : /* @__PURE__ */ React.createElement("div", { className: "header-cta" }, /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(SpaceBetween, { size: "xl" }, /* @__PURE__ */ React.createElement(Box, { variant: "h2", padding: "n" }, "Discover Resources"), /* @__PURE__ */ React.createElement(Box, { variant: "p", padding: "n" }, "Get started by importing regions from one or more of your accounts."), /* @__PURE__ */ React.createElement(Button, { onClick: () => handleFollow({
      detail: { href: IMPORT, external: false }
    }), variant: "primary" }, "Import"))))
  ))), /* @__PURE__ */ React.createElement(Box, { padding: { top: "xxxl", horizontal: "s" } }, /* @__PURE__ */ React.createElement(
    Grid,
    {
      gridDefinition: [
        {
          colspan: { xl: "6", l: "5", s: "6", xxs: "10" },
          offset: { l: "2", xxs: "1" }
        },
        {
          colspan: { xl: "2", l: "3", s: "4", xxs: "10" },
          offset: { s: "0", xxs: "1" }
        }
      ]
    },
    /* @__PURE__ */ React.createElement(SpaceBetween, { size: "xxl" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Box, { variant: "h1", tagOverride: "h2", padding: { bottom: "s", top: "n" } }, "How it works"), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
      "img",
      {
        style: { width: "100%", height: "100%" },
        src: `/icons/architecture-diagram.png`
      }
    ))), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Box, { variant: "h1", tagOverride: "h2", padding: { bottom: "s", top: "n" } }, "Benefits and features"), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(ColumnLayout, { columns: 2, variant: "text-grid" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Box, { variant: "h3", padding: { top: "n" } }, "Build architecture diagrams"), /* @__PURE__ */ React.createElement(Box, { variant: "p" }, "Workload Discovery on AWS lets you build, customize, and share detailed architecture diagrams. Workload Discovery on AWS maintains an inventory of the AWS resources across your accounts and Regions, mapping relationships between them, and displaying them in a web UI.")), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Box, { variant: "h3", padding: { top: "n" } }, "Query AWS Cost & Usage Reports (CURs)"), /* @__PURE__ */ React.createElement(Box, { variant: "p" }, "The cost query builder lets you locate AWS resources and services that may have incurred a cost. The estimated cost data is automatically calculated for the time period specified and displays on your architecture diagrams. You can generate a cost report for your architecture diagrams that contains an overview of the estimated cost and export them as CSV.")), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Box, { variant: "h3", padding: { top: "n" } }, "Search"), /* @__PURE__ */ React.createElement(Box, { variant: "p" }, "The search feature lets you use basic information, such as resource name, Tag name, or IP address to locate the resources you are interested in.")), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Box, { variant: "h3", padding: { top: "n" } }, "Resource directory"), /* @__PURE__ */ React.createElement(Box, { variant: "p" }, "Explore resources provisioned across your accounts and Regions using the resource directory. It contains all the resources Workload Discovery on AWS has discovered. You can start building your Workload Discovery on AWS architecture diagrams by selecting a resource in the web UI.")))))),
    /* @__PURE__ */ React.createElement("div", { css: sidebarStyle }, /* @__PURE__ */ React.createElement(SpaceBetween, { size: "xxl" }, /* @__PURE__ */ React.createElement(
      Container,
      {
        header: /* @__PURE__ */ React.createElement(Header, { variant: "h2" }, "Getting started ", /* @__PURE__ */ React.createElement(Icon, { name: "external" }))
      },
      /* @__PURE__ */ React.createElement("ul", { "aria-label": "Additional resource links", css: listStyle }, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(Link, { href: RESOURCES, onFollow: handleFollow }, "Explore Resources")), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(Link, { href: DIAGRAM_MANAGEMENT, onFollow: handleFollow }, "Manage Diagrams")), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(Link, { href: ACCOUNTS, onFollow: handleFollow }, "Import Accounts")), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
        Link,
        {
          external: true,
          externalIconAriaLabel: "Opens in a new tab",
          href: "https://docs.aws.amazon.com/solutions/latest/workload-discovery-on-aws/welcome.html"
        },
        "Implementation Guide"
      )))
    ), /* @__PURE__ */ React.createElement(
      Container,
      {
        header: /* @__PURE__ */ React.createElement(Header, { variant: "h2" }, "More resources ", /* @__PURE__ */ React.createElement(Icon, { name: "external" }))
      },
      /* @__PURE__ */ React.createElement("ul", { "aria-label": "Additional resource links", css: listStyle }, /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
        Link,
        {
          external: true,
          externalIconAriaLabel: "Opens in a new tab",
          href: "https://github.com/awslabs/aws-perspective"
        },
        "GitHub repository"
      )), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
        Link,
        {
          external: true,
          externalIconAriaLabel: "Opens in a new tab",
          href: "https://github.com/awslabs/aws-perspective/issues/new?assignees=&labels=enhancement&template=feature_request.md&title="
        },
        "Feature request"
      )), /* @__PURE__ */ React.createElement("li", null, /* @__PURE__ */ React.createElement(
        Link,
        {
          external: true,
          externalIconAriaLabel: "Opens in a new tab",
          href: "https://github.com/awslabs/aws-perspective/issues/new?assignees=&labels=bug&template=bug_report.md&title="
        },
        "Raise an issue"
      )))
    )))
  )));
};
export default Homepage;
